// Copying one section of a client document from another saved document of the same kind: which
// sections can be copied, the content each one owns, and a short preview of it for the picker.
import { format, isValid, parseISO } from 'date-fns';
import {
	PROPOSAL_DOCUMENT_TYPE,
	formatGbpAmount,
	formatProposalDate,
} from '@/utils/proposalDocument';
import { QUOTATION_DOCUMENT_TYPE } from '@/utils/quotationDocument';
import { ASSIGNMENT_CONFIRMATION_DOCUMENT_TYPE } from '@/utils/assignmentConfirmationDocument';
import {
	getClientDocumentCounterparty,
	getClientDocumentDate,
	getClientDocumentReference,
	getClientDocumentSiteAddress,
	normalizeClientDocumentContent,
} from '@/utils/clientDocumentRegistry';

const text = (value) => String(value ?? '').trim();
const texts = (values) => (Array.isArray(values) ? values.map(text).filter(Boolean) : []);
const list = (value) => (Array.isArray(value) ? value : []);
const joinParts = (parts) => parts.map(text).filter(Boolean).join(' · ');
const amount = (value) => (text(value) ? formatGbpAmount(value) : '');
const percent = (rate) => {
	const value = Number(rate);
	return text(rate) && Number.isFinite(value) ? `${Math.round(value * 1000) / 10}%` : '';
};
// Label and value pairs that show only when the value is set.
const fields = (entries) => entries.filter(([, value]) => text(value));

// One part of a preview: an optional title with paragraphs, bullets, label/value rows and a note.
const previewBlock = ({ title = '', paragraphs = [], bullets = [], rows = [], note = '' } = {}) => ({
	title: text(title),
	paragraphs: texts(paragraphs),
	bullets: texts(bullets),
	rows: rows.map(([label, value]) => [text(label), text(value)]).filter(([label, value]) => label || value),
	note: text(note),
});

const hasBody = (block) =>
	block.paragraphs.length > 0 || block.bullets.length > 0 || block.rows.length > 0 || Boolean(block.note);

const paragraphsPreview = (paragraphs) => [previewBlock({ paragraphs })];
const bulletsPreview = (bullets) => [previewBlock({ bullets })];
const scheduleRowsPreview = (rows) => list(rows).map((row) => [row?.label, row?.value]);
const signatoryLines = (signatories, keys) =>
	list(signatories).map((signatory) => joinParts(keys.map((key) => signatory?.[key])));

const PROPOSAL_SECTIONS = [
	{
		id: 'properties',
		label: 'Properties',
		paths: [['properties']],
		preview: (content) =>
			bulletsPreview(list(content.properties).map((property) => joinParts([property?.name, property?.address]))),
	},
	{
		id: 'summary',
		label: 'Proposal summary',
		paths: [['summaryParagraphs']],
		preview: (content) => paragraphsPreview(content.summaryParagraphs),
	},
	{
		id: 'areas',
		label: 'Service areas',
		paths: [['serviceAreas']],
		preview: (content) =>
			list(content.serviceAreas).map((area) => previewBlock({ title: area?.title, bullets: area?.items })),
	},
	{
		id: 'tasks',
		label: 'Tasks',
		paths: [['taskSections']],
		preview: (content) =>
			list(content.taskSections).map((section) =>
				previewBlock({ title: section?.title, bullets: section?.items, note: section?.footnote }),
			),
	},
	{
		id: 'scope',
		label: 'Scope clarification',
		paths: [['scopeClarification']],
		preview: (content) => [
			previewBlock({
				title: content.scopeClarification?.title,
				paragraphs: content.scopeClarification?.paragraphs,
			}),
		],
	},
	{
		id: 'schedule',
		label: 'Cleaning schedule',
		paths: [['schedule', 'intro'], ['schedule', 'rows']],
		preview: (content) => [
			previewBlock({ paragraphs: [content.schedule?.intro], rows: scheduleRowsPreview(content.schedule?.rows) }),
		],
	},
	// Pricing itself is calculated from the schedule and the rates; only its notes are copied.
	{
		id: 'pricing-notes',
		label: 'Pricing notes',
		paths: [['pricing', 'notes']],
		preview: (content) => bulletsPreview(content.pricing?.notes),
	},
	{
		id: 'text',
		label: 'Text sections',
		paths: [['textSections']],
		preview: (content) =>
			list(content.textSections).map((section) =>
				previewBlock({ title: section?.title, paragraphs: section?.paragraphs }),
			),
	},
	{
		id: 'acceptance',
		label: 'Acceptance',
		paths: [['acceptance', 'paragraphs'], ['acceptance', 'signatureLabel'], ['acceptance', 'signatories']],
		preview: (content) => [
			previewBlock({ paragraphs: content.acceptance?.paragraphs }),
			previewBlock({
				title: 'Signatories',
				bullets: signatoryLines(content.acceptance?.signatories, ['label', 'name', 'role']),
			}),
		],
	},
	{
		id: 'footer',
		label: 'Document footer',
		paths: [['footerText']],
		preview: (content) => paragraphsPreview([content.footerText]),
	},
];

const QUOTATION_SECTIONS = [
	{
		id: 'client',
		label: 'Client and service',
		paths: [['clientId'], ['clientName'], ['clientAddress'], ['serviceDetails']],
		preview: (content) => [
			previewBlock({
				rows: fields([
					['Client', content.clientName],
					['Client address', content.clientAddress],
					['Service type', content.serviceDetails?.serviceType],
					['Property', content.serviceDetails?.property],
					['Site address', content.serviceDetails?.siteAddress],
					['Scope of work', content.serviceDetails?.scopeOfWork],
				]),
			}),
		],
	},
	{
		id: 'schedule',
		label: 'Work schedule',
		paths: [['schedule', 'phases']],
		preview: (content) =>
			bulletsPreview(
				list(content.schedule?.phases).map((phase) =>
					joinParts([phase?.title, formatProposalDate(phase?.date), phase?.team, phase?.time]),
				),
			),
	},
	{
		id: 'costs',
		label: 'Cost breakdown',
		paths: [['costBreakdown', 'rows'], ['costBreakdown', 'vatRate'], ['costBreakdown', 'additionalTerms']],
		preview: (content) => {
			const costs = content.costBreakdown || {};
			const rows = list(costs.rows).map((row) => [row?.item, amount(row?.amount)]);
			const vatRate = percent(costs.vatRate);
			return [
				// The VAT rate alone is the template's default, so it shows only next to cost rows.
				previewBlock({ rows, note: rows.length && vatRate ? `VAT ${vatRate}` : '' }),
				previewBlock({ paragraphs: [costs.additionalTerms] }),
			];
		},
	},
	{
		id: 'payment-terms',
		label: 'Payment terms',
		paths: [['paymentTerms', 'text']],
		preview: (content) => paragraphsPreview([content.paymentTerms?.text]),
	},
	{
		id: 'acceptance',
		label: 'Acceptance',
		paths: [['acceptance', 'text'], ['acceptance', 'validityText']],
		preview: (content) => paragraphsPreview([content.acceptance?.text, content.acceptance?.validityText]),
	},
	{
		id: 'footer',
		label: 'Document footer',
		paths: [['footerText']],
		preview: (content) => paragraphsPreview([content.footerText]),
	},
];

const ASSIGNMENT_CONFIRMATION_SECTIONS = [
	{
		id: 'setup',
		label: 'Assignment setup',
		paths: [
			['subcontractorId'],
			['subcontractorName'],
			['subcontractorMobile'],
			['propertyId'],
			['propertyName'],
			['propertyAddress'],
			['serviceId'],
			['serviceType'],
		],
		preview: (content) => [
			previewBlock({
				rows: fields([
					['Subcontractor', content.subcontractorName],
					['Property', content.propertyName],
					['Service', content.serviceType],
					['Subcontractor mobile', content.subcontractorMobile],
					['Property address', content.propertyAddress],
				]),
			}),
		],
	},
	{
		id: 'areas',
		label: 'Service areas',
		paths: [['scope', 'serviceAreas']],
		preview: (content) => bulletsPreview(content.scope?.serviceAreas),
	},
	{
		id: 'regular-tasks',
		label: 'Regular cleaning tasks',
		paths: [['scope', 'regularTasks']],
		preview: (content) => bulletsPreview(content.scope?.regularTasks),
	},
	{
		id: 'monthly-tasks',
		label: 'Monthly tasks',
		paths: [['scope', 'monthlyTasksEnabled'], ['scope', 'monthlyTasks'], ['scope', 'monthlyFootnote']],
		preview: (content) => [
			previewBlock({
				bullets: content.scope?.monthlyTasks,
				note: content.scope?.monthlyTasksEnabled
					? content.scope?.monthlyFootnote
					: 'Monthly tasks are switched off in this document.',
			}),
		],
	},
	{
		id: 'schedule',
		label: 'Cleaning schedule',
		paths: [['schedule', 'rows']],
		preview: (content) => [previewBlock({ rows: scheduleRowsPreview(content.schedule?.rows) })],
	},
	{
		id: 'pricing-notes',
		label: 'Pricing notes',
		paths: [['pricing', 'notes']],
		preview: (content) => bulletsPreview(content.pricing?.notes),
	},
	{
		id: 'site',
		label: 'Site information',
		paths: [['siteInformation', 'paragraphs']],
		preview: (content) => paragraphsPreview(content.siteInformation?.paragraphs),
	},
	{
		id: 'terms',
		label: 'Terms',
		paths: [['terms', 'paragraphs']],
		preview: (content) => paragraphsPreview(content.terms?.paragraphs),
	},
	{
		id: 'contact',
		label: 'Contact',
		paths: [['contact', 'paragraphs']],
		preview: (content) => paragraphsPreview(content.contact?.paragraphs),
	},
	{
		id: 'acceptance',
		label: 'Acceptance',
		paths: [['acceptance', 'intro'], ['acceptance', 'checklist']],
		preview: (content) => [
			previewBlock({ paragraphs: [content.acceptance?.intro], bullets: content.acceptance?.checklist }),
		],
	},
	{
		id: 'signatures',
		label: 'Signatures',
		paths: [['acceptance', 'signatories']],
		preview: (content) =>
			bulletsPreview(signatoryLines(content.acceptance?.signatories, ['label', 'name', 'role', 'mobile'])),
	},
	{
		id: 'footer',
		label: 'Document footer',
		paths: [['footerText']],
		preview: (content) => paragraphsPreview([content.footerText]),
	},
];

const SECTIONS_BY_TYPE = {
	[PROPOSAL_DOCUMENT_TYPE]: PROPOSAL_SECTIONS,
	[QUOTATION_DOCUMENT_TYPE]: QUOTATION_SECTIONS,
	[ASSIGNMENT_CONFIRMATION_DOCUMENT_TYPE]: ASSIGNMENT_CONFIRMATION_SECTIONS,
};

export const getImportableSections = (documentType) => SECTIONS_BY_TYPE[documentType] || [];

export const getImportableSection = (documentType, sectionId) =>
	getImportableSections(documentType).find((section) => section.id === sectionId) || null;

const cloneValue = (value) => (value === undefined ? undefined : JSON.parse(JSON.stringify(value)));

const getAtPath = (root, path) =>
	path.reduce((value, key) => (value === null || value === undefined ? undefined : value[key]), root);

const setAtPath = (root, path, value) => {
	const parent = path.slice(0, -1).reduce((node, key) => {
		if (!node[key] || typeof node[key] !== 'object') node[key] = {};
		return node[key];
	}, root);
	parent[path[path.length - 1]] = value;
};

/** Copies of the section's values, one per content path. */
export const readSectionValues = (content, section) =>
	section.paths.map((path) => cloneValue(getAtPath(content, path)));

/** Puts values read with readSectionValues back into the content, in place. */
export const writeSectionValues = (content, section, values) => {
	section.paths.forEach((path, index) => setAtPath(content, path, cloneValue(values[index])));
};

/** Replaces the section in target with the source's; returns what was there, for undo. */
export const importSection = (target, source, section) => {
	const previous = readSectionValues(target, section);
	writeSectionValues(target, section, readSectionValues(source, section));
	return previous;
};

/** The section as the picker shows it; title-only blocks stay so section names are visible. */
export const previewSection = (content, section) =>
	section.preview(content || {}).filter((block) => block.title || hasBody(block));

const isPreviewEmpty = (blocks) => !blocks.some(hasBody);

const sameSection = (a, b, section) =>
	JSON.stringify(readSectionValues(a, section)) === JSON.stringify(readSectionValues(b, section));

// Copies of one document often share a reference and a date; when each was last edited tells them apart.
const editedLabel = (value) => {
	const date = value ? parseISO(String(value)) : null;
	return date && isValid(date) ? format(date, 'd MMM yyyy, HH:mm') : '';
};

const placeOf = (documentType, content, document) => {
	if (documentType === PROPOSAL_DOCUMENT_TYPE) {
		const names = list(content.properties).map((property) => text(property?.name)).filter(Boolean);
		if (names.length) return names.join(' · ');
	}
	if (documentType === ASSIGNMENT_CONFIRMATION_DOCUMENT_TYPE && text(content.propertyName)) {
		return text(content.propertyName);
	}
	return getClientDocumentSiteAddress({ ...document, documentType, content });
};

/**
 * A saved document as the picker lists it. origin is 'client' for client documents and 'supplier'
 * for assignment confirmations moved to supplier documents.
 */
export const toSectionImportDocument = (document = {}, { documentType, origin = 'client' } = {}) => {
	const content = normalizeClientDocumentContent(document.content || {}, documentType);
	const id = origin === 'supplier' ? document.id : document.documentId ?? document.id;
	const counterparty =
		getClientDocumentCounterparty({ ...document, documentType, content }) ||
		texts(document.supplierNames).join(', ');
	const date = getClientDocumentDate({ ...document, documentType, content }) || null;
	return {
		key: `${origin}:${id}`,
		origin,
		reference: text(getClientDocumentReference({ ...document, documentType, content })),
		date,
		dateLabel: formatProposalDate(date),
		counterparty: text(counterparty),
		place: text(placeOf(documentType, content, document)),
		archived: Boolean(document.isArchived),
		updatedAt: document.updatedAt || document.createdAt || null,
		editedLabel: editedLabel(document.updatedAt || document.createdAt),
		content,
	};
};

const timestamp = (value) => {
	const time = value ? Date.parse(value) : Number.NaN;
	return Number.isFinite(time) ? time : 0;
};

/**
 * The picker's list for one section: newest document first, each with its preview and whether
 * the section is empty there or already the same as in the current document.
 */
export const buildSectionImportSources = ({ section, documents = [], currentContent = {} } = {}) =>
	documents
		.map((document) => {
			const preview = previewSection(document.content, section);
			return {
				...document,
				preview,
				empty: isPreviewEmpty(preview),
				same: sameSection(document.content, currentContent, section),
			};
		})
		.sort(
			(a, b) =>
				timestamp(b.date) - timestamp(a.date) ||
				timestamp(b.updatedAt) - timestamp(a.updatedAt),
		);

/** Text the picker's search looks through. */
export const sectionImportSearchText = (source = {}) =>
	[source.reference, source.counterparty, source.place, source.dateLabel].map(text).join(' ').toLowerCase();
